// ---------------------------------------------------------------------------
// Test Mode — person employment & payroll builders (demo audit).
//
// Must match the shapes from employmentApi.ts (PersonEmployment) and
// payrollApi.ts (PayrollEntry).
// ---------------------------------------------------------------------------

import { isoDateTime } from './company';
import { PEOPLE_REFS } from './people';
import type { PersonEmployment, EmploymentType } from '../lib/employmentApi';
import type { PayrollEntry } from '../lib/payrollApi';

/**
 * Employment data matching the PersonEmployment interface.
 * getEmployment() reads `res.data.employment` from the API envelope.
 */
export function buildPersonEmployment(personId: string): { employment: PersonEmployment | null } {
  const idx = PEOPLE_REFS.findIndex((p) => p.id === personId);
  if (idx < 0) return { employment: null };

  const isEmployee = idx < 6;
  const type: EmploymentType = isEmployee ? 'EMPLOYEE' : 'CONTRACTOR';

  return {
    employment: {
      type,
      companyName: isEmployee ? null : 'Clean Code Culture SRL',
      companyCui: isEmployee ? null : 'RO12345678',
      companyRegCom: isEmployee ? null : 'J12/100/2019',
      companyAddress: isEmployee ? null : 'Str. Exemplu nr. 1, Cluj-Napoca',
      iban: isEmployee ? null : `RO49BTRL0123456789${String(idx).padStart(4, '0')}`,
      hasCim: isEmployee,
      cimFileName: isEmployee ? 'CIM_semnat.pdf' : null,
      cimMimeType: isEmployee ? 'application/pdf' : null,
      cimFileSize: isEmployee ? 245_760 : null,
      cimUploadedAt: isEmployee ? isoDateTime(-300, 10) : null,
    },
  };
}

/**
 * Payroll entries matching the PayrollEntry interface from payrollApi.ts.
 * getPersonPayroll() reads `res.data.entries` from the API envelope.
 */
export function buildPersonPayroll(personId: string): { entries: PayrollEntry[] } {
  const idx = PEOPLE_REFS.findIndex((p) => p.id === personId);
  const isEmployee = idx >= 0 && idx < 6;

  if (!isEmployee) return { entries: [] };

  const person = PEOPLE_REFS[idx];
  const baseGross = 12000 + (idx % 5) * 2000;
  const now = new Date();
  const entries: PayrollEntry[] = [];

  for (let m = 0; m < 6; m++) {
    const d = new Date(now.getFullYear(), now.getMonth() - m, 1);
    const gross = baseGross + (m % 2) * 500;
    const employeeContributions = Math.round(gross * 0.35); // CAS + CASS
    const incomeTax = Math.round((gross - employeeContributions) * 0.1);
    const net = gross - employeeContributions - incomeTax;
    const employerCam = Math.round(gross * 0.0225);
    const totalCost = gross + employerCam;

    entries.push({
      id: `payroll-${personId}-${d.getFullYear()}-${d.getMonth() + 1}`,
      documentId: `doc-payroll-${d.getFullYear()}-${d.getMonth() + 1}`,
      documentFileName: `Stat_plata_${String(d.getMonth() + 1).padStart(2, '0')}_${d.getFullYear()}.pdf`,
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      extractedName: person?.name ?? 'Necunoscut',
      grossSalary: gross,
      netSalary: net,
      employeeContributions,
      incomeTax,
      employerCam,
      totalCost,
      currency: 'RON',
    });
  }

  return { entries };
}
